// The contract between the DOM overlay and main.ts: the fleet as the chrome
// reads it, the intents a control may send, the audio feed, and the pick
// modes. `ui/overlay.ts` re-exports every name, so an importer keeps its path.

import type {
  AnchorState, Building, ChatChannel, FleetOp, HeatmapKind, Mission, ModuleFamily,
  Brotherhood, NpcKind, ReplayRef, ShipClass, SiegeOp, SignatureCharge, Condition, Waypoint, PlotOp, NameSeamount, CacheAction, SeaObjectStock,
} from '../../gen/proto/aquaphobia';
// The casino's intents are declared beside the sender, so the overlay states
// the wire's vocabulary and reaches the socket through `UIActions` alone.
import type { CasinoIntent } from '../../net/connection';
import type { ReportReason } from '../reportdraft';

/**
 * What a REPORT of the game client names (deployment.md §7.6, R1323): a
 * line of the WORLD, FACTION or SQUAD channel by its line id, a commander
 * by the account id, or a hull name of the faction by the fleet id. The
 * game server copies the piece; the client never sends the words.
 */
export type GameReport =
  | { kind: 'line'; lineId: number }
  | { kind: 'commander'; accountId: number }
  | { kind: 'fleet'; fleetId: number }
  /** A seamount name by the seamount id of the map (R2620, R2633). */
  | { kind: 'seamount'; body: number }
  /** A free-text cache note by the cache id (v2 §9.4, R2631, R2649). */
  | { kind: 'cache'; cache: number };

/**
 * The approach state of one hull (numbers.md §4.1, R2371, R2376). Every
 * world plays the approach (R2400). The server sends
 * the bow heading and the charge for every hull the viewer hears, and the
 * other fields for an own hull alone (0 or false elsewhere).
 */
export interface FleetApproach {
  /** The bow heading in milliradians (`FleetState.heading_mrad`). */
  headingMrad: number;
  /** Own hulls only: the break contact mends the shield now (R2458). The
   *  server sets it while the hull is Quiet, the calm gate is still shut,
   *  the world mends shields and the shield is not full. */
  quiet: boolean;
  charge: SignatureCharge;
  /** Own hulls only: an order effect holds now, and its MAP seconds left
   *  (0 for DIG IN, which has no clock). */
  active: boolean;
  leftSec: number;
  /** Own hulls only: the MAP seconds left on the recharge floor of a spent
   *  charge when the frame came (`signature_floor_sec`, R2405), and the
   *  `Date.now()` of that frame, so the card counts the floor down. */
  floorSec: number;
  floorAtMs: number;
  /** Own hulls only: the floor stands still at `floorSec`, because a
   *  CANISTER or BROADSIDE effect holds (R2412). Absent reads false. */
  floorHeld?: boolean;
}


/** A fleet as the UI needs it (fed by the fleet render layer). */
export interface UIFleet {
  id: number;
  name: string;
  /** The name part is the owner's own words, not a faction pool name (R1886). */
  customName: boolean;
  cls: ShipClass;
  hullPct: number;
  shieldPct: number;
  /**
   * The hull's own maximum hull and shield in points (R1278). A module, a
   * typed shield and the tree move them off the class row. 0 = not sent.
   */
  hullMax: number;
  shieldMax: number;
  mission: Mission;
  captainId: number;
  hold: { resource: number; amount: number }[];
  cargoCap: number;
  captureLeftSec: number;
  /**
   * The anchor's four-state machine, its running set/weigh cycle, and the
   * map seconds of the beam order of a SET Beacon, 0 while the beam is off
   * (R114, R2858).
   */
  anchor: AnchorState;
  anchorLeftSec: number;
  beamLeftSec: number;
  escorting: number;
  /** The hostile this hull chases and fires at alone; 0 = none (R544). */
  focus: number;
  weapType: number;
  shieldType: number;
  /** The rounds aboard, all kinds together: the sum of `rounds`. */
  torpedoes: number;
  reloadSec: number;
  /** The whole reload of one tube, map seconds, own fleets only; absent or
   *  0 when the frame sent none. The TUBES vital fills the loading tube
   *  from it (R1853). */
  reloadFullSec?: number;
  torpedoRange: number;
  /** The kind the next shot fires (R2017): the active round while it holds
   *  a round, else the next kind of the fixed order (`ui/rounds.ts`). */
  firingKind: number;
  /** Own fleets only (R2017): the load by kind in the fixed order, and the
   *  active round the commander picked (0 = none). Absent reads as none. */
  rounds?: readonly { kind: number; count: number }[];
  activeRound?: number;
  slowed: boolean;
  deep: boolean;
  /** The A8 deep-run gait (R721). Own fleets only. */
  deepRun: boolean;
  /** Own haulers only: the hull works a shaft on a worked-out field
   * (Deep Mining, I2 — R715). */
  shaft: boolean;
  /** Own haulers only: the owner switched the dredger brain off for this
   * hull (R1226). The wire default, false, reads as ON. */
  autoFieldOff: boolean;
  /** Own armed hulls only: the fleet holds fire (R1437). Its gun takes
   * no target by itself and fires at a focus alone. */
  holdFire: boolean;
  /** Own hulls only (R2584, R2617): the hull is a
   *  command ship whose formation is on. Absent reads as off. */
  formation?: boolean;
  /** Own hulls only: the fleet id of the command ship
   *  this hull keeps its formation station by; absent or 0 for none. */
  formationOf?: number;
  /** Own hulls only: a hostile faction hears the hull now
   * (`FleetState.heard`, R2458). */
  heard: boolean;
  x: number;
  y: number;
  /** Projection fatigue (numbers.md §4, R80): whole percent, 0 = not sent. */
  readinessPct: number;
  /** The lowest readiness this hull can reach (§4, R657, R769): whole
   * percent, 0 = not sent. The captain aboard lifts it. */
  readinessFloorPct: number;
  /** The hull's live noise in thousandths (R1500), own fleets only;
   * 0 = not sent. The server computes it; the client mirrors no term. */
  noiseMilli: number;
  /** Map seconds until the shield regen starts (R1503), own fleets only,
   * run down between frames; 0 = the shield is full, the regen runs, or
   * the frame did not say. The server computes it. */
  regenInSec: number;
  /** Vedette ping cooldown in game-seconds, 0 = ready (plan §4.5, R83). */
  scanReadySec: number;
  /** Map seconds until this Vedette's A6 noisemaker is ready, 0 = ready
   * (R722, R1507). Own fleets only. */
  decoyReadySec: number;
  /** Map seconds this Oracle still broadcasts its array; 0 = pressable
   * (R453). Own fleets only. */
  shareSec: number;
  /** The owning commander's account id, own faction only; 0 = not sent. It
   * is what the squad test matches against (R199). */
  ownerPlayer: number;
  /** The owning commander's name, own faction only; empty = not sent. */
  ownerName: string;
  /** The approach state (R2376), absent on a frame that carries none. */
  approach?: FleetApproach;
  /** The conditions of an own fleet (v2 §6.1, R2576), absent while the
   *  frame carries none. */
  conditions?: readonly Condition[];
  /** The course of an own fleet (v2 §7, R2576): the legs with leg 0 at
   *  the order point, the leg it sails to now and the pass of its loop
   *  part. Absent for an order that is no course. */
  course?: { legs: readonly Waypoint[]; leg: number; pass: number };
  /** Own hulls only, while `depth` is latched (v2 §3.2, R2625, R2637): the
   *  depth rating the hull carries everywhere, R4 counted. Absent while
   *  the frame carries none; the card then reads the class rating. */
  depthRating?: number;
  /** Own hulls only, while `treasure` is latched (v2 §9.1, R2643, R2649):
   *  the search hint, 1 to 3 from far to near; absent for none. It names
   *  no position. */
  searchHint?: number;
  /** Own hulls only: the map seconds a SEARCH still works at
   *  its point, counted down from the last frame (R2649); absent while
   *  the fleet sails there or flies another mission. */
  workLeftSec?: number;
  /** The NPC kind of a hull of the NPC side (`FleetState.npc`, v2 §8.1,
   *  R2637); absent for a hull of a faction. Public, as the faction is. */
  npc?: NpcKind;
  /** The brotherhood of a pirate hull (`FleetState.brotherhood`, v2 §8.5,
   *  R2720); absent for every other hull. */
  brotherhood?: Brotherhood;
  /** A boarding (v2 §8.6, R2721): on a pirate the fleet it boards, on a
   *  boarded hull the pirate, and the share done now, 0 to 1. Absent while
   *  no boarding runs. */
  boardingTarget?: number;
  boardedBy?: number;
  boardingShare?: number;
  /** Own Sappers only, while `sapper` is latched (v2 §11.1, R2654): the
   *  stow aboard, one row for each kind; absent for an empty stow. */
  stow?: readonly SeaObjectStock[];
  /** Own Sappers only: the map seconds to the next LAY and the map
   *  seconds the loud lay still runs, counted down from the last frame;
   *  absent at 0 (R2654). */
  layReadySec?: number;
  loudLeftSec?: number;
}

export interface UIActions {
  build(id: Building): void;
  buildShips(cls: ShipClass, count: number): void;
  buildTorpedoes(kind: number, count: number): void;
  /** A batch of one kind of sea object for the Depot (R2653): the
   *  Enqueue of a sea object job. */
  orderSeaObjects(kind: number, count: number): void;
  research(id: number): void;
  cancelJob(id: number): void;
  moveJob(id: number, dir: number): void;
  rushJob(id: number): void;
  deploy(cls: ShipClass, count: number, captainId: number): void;
  /** Deploy one hull of the pens with the preparation it keeps (R84): the
   * captain aboard and its saved load mix (R2017). */
  deployDesign(fleetId: number): void; // the pens hold objects: a deploy names the hull
  fleetOp(fleetId: number, op: FleetOp, arg?: number): void;
  /** The load mix of a docked hull (R2017, R2031): `FLEET_OP_SET_LOAD`
   *  with one row for each kind; an empty mix is AUTO. */
  setLoad(fleetId: number, mix: readonly { kind: number; count: number }[]): void;
  /** The stow of a Sapper in the pens (R2654, R2658): `FLEET_OP_SET_STOW`
   *  with one row for each kind; an empty stow is AUTO. */
  setStow(fleetId: number, stow: readonly { kind: number; count: number }[]): void;
  repair(fleetId: number): void; // wear is per fleet: the job names one hull
  installPrint(design: number): void;
  setMission(fleetId: number, mission: Mission): void;
  recruit(id: number): void;
  dismiss(id: number): void;
  ransom(id: number): void;
  /** Free a prisoner of the Brig: the captain id and the owner's id
   *  (R2743). */
  releaseCaptain(id: number, owner: number): void;
  selectFleet(id: number): void;
  focus(x: number, y: number): void;
  focusCell(cell: number): void;
  /** VIEW of a lane row (R2709): the camera fits the points of the lane,
   *  and the chart makes the lane of the mark id stand out. */
  viewLane(markId: string, pts: readonly { x: number; y: number }[]): void;
  /** Selects one site, so the context slot draws its card. */
  selectSite(id: number): void;
  /** A press of a DEPOT family head (R3244): the map drops the selection,
   *  centres on the own sea object `id` at world px (x, y) and pins its
   *  peek, as a press on its mark does. */
  showSeaObject(x: number, y: number, id: number): void;
  /** Leaves the map for the sea view at the selected fleet or the centre
   *  of the screen (R2493). Only the game page opens the sea view; a page
   *  that leaves it out shows no SEA VIEW row. */
  seaView?(): void;
  train(captainId: number, attr: number): void;
  gatherAt(fleetId: number, cell: number): void;
  say(channel: ChatChannel, text: string): void;
  siege(op: SiegeOp, siteId: number, fleetId: number): void;
  aim(fleetId: number): void;
  invent(hull: ShipClass, modules: { family: ModuleFamily; tier: number }[]): void;
  print(master: number, runs: number): void;
  market(a: { listDesign?: number; price?: number; buy?: number; cancel?: number;
    listIsotopes?: number }): void;
  /**
   * One casino intent (casino.md §5). The six wire messages travel as one
   * discriminated union, so the CASINO panel states what it means and
   * main.ts puts it on the socket. The client rules on no bet: a refusal
   * comes back as a notice and the truth in the next table frame.
   */
  casino(a: CasinoIntent): void;
  /** Ask for the inbox list (v12); sent when the INBOX panel opens. */
  inboxFetch(): void;
  /** Mark one message (or all) read. */
  inboxRead(id: number, all: boolean): void;
  /** Mark a set of messages read with one action, which the world answers
   *  with one list (`InboxAction.mark_ids`, inbox.md §3, R1851): MARK THESE
   *  READ of a range. */
  inboxReadMany(ids: readonly number[]): void;
  /** REPLACE: press the Replace action of one loss message (R1308). */
  inboxReplace(msgId: number): void;
  /** Ask for the inbox ledger of the seat (inbox.md §5.6): SINCE YOU LEFT
   *  and the DIGEST sum it (§7, R1851). Sent on an event, never on a timer. */
  inboxLedgerQuery(): void;
  /** Whether the world socket is open, so an intent sent now arrives. A
   *  press that must reach the world asks first, and tells the player when
   *  the link is down (audit P2, R1851). */
  linkUp(): boolean;
  /** Ask for the replay of a window of the combat log; the viewer opens on
   * the answering frame (R526). The reference is the server's own, and the
   * client sends it back unchanged. */
  openReplay(ref: ReplayRef): void;
  admin(op: string, value?: number): void;
  scrapDesign(id: number): void;
  /** Roll one slot of a master again (numbers.md §6, R1194). The server
   * files a new master with the next Mk number and keeps the source. */
  redraft(design: number, slot: number): void;
  useDataCore(): void;
  expedition(depth: number, captainId: number): void;
  /** Ask the server for the WAR dock's data (plan §4, R83). */
  warQuery(): void;
  /** Ask the server for the empire's income ring (R1489). */
  productionQuery(): void;
  /** Ask the server for the levels past the next of one building: the
   *  level picker of the build card (R1839). */
  previewQuery(building: number): void;
  /** Ask the server for the season timelapse so far (chronicle.md §10.3,
   *  R1620): WAR · SOVEREIGNTY plays it. */
  timelapseQuery(): void;
  /** Subscribe the map to one heatmap mode, or NONE for the plain map
   * (v13). Sending the mode that is already on turns it off again. */
  setHeatmap(kind: HeatmapKind): void;
  /** Ask the server to turn the empire's auto-rebuild wish on or off. The
   * server holds the value and echoes it in the next Empire frame. */
  setAutoRebuild(on: boolean): void;
  /** The advisor switch of this connection (R1305). */
  setAdvisor(on: boolean): void;
  /** Close the welcome card for good (R1572): the world keeps the mark on
   *  the empire, so no device of the season shows the card again. */
  closeWelcome(): void;
  /**
   * Put the selected own fleet on one control group (R539). The server is
   * the store: the map sends the intent and writes its own picture at once,
   * and the next Empire frame states the truth.
   */
  assignGroup(slot: number): void;
  /**
   * Put one named own fleet on one control group (R1817): the step of
   * UNDO after a long press replaced a slot. The same intent as
   * `assignGroup`, for a fleet that is not the selection.
   */
  setGroup(slot: number, fleetId: number): void;
  /**
   * Empty one control group: a press on the slot the selected fleet
   * already holds, in the fleet card's group list. The wire states it as
   * the slot with fleet 0.
   */
  releaseGroup(slot: number): void;
  /**
   * Give one own hull a new name part (monetization.md §3.2, R1250). The
   * server keeps the designator prefix and states the new hull name in the
   * next frames; a refusal arrives as an error notice.
   */
  renameFleet(fleetId: number, name: string): void;
  /** Name one seamount the commander's hull found (v2 §4.3, R2626): the
   *  free text or the two places of the word list. A refusal arrives as an
   *  error notice. */
  nameSeamount(n: NameSeamount): void;
  /** One course to one own fleet (v2 §7, R2589): the legs in world px
   *  with the loop flag and the pass count of each. */
  sendCourse(fleetId: number, legs: readonly { x: number; y: number; loop: boolean; passes: number }[]): void;
  /** One intent on the course plots (R2576, R2589): SAVE, RENAME, DELETE. */
  plotAction(op: PlotOp, a: { plotId?: number; name?: string; legs?: readonly { x: number; y: number; loop: boolean; passes: number }[]; lane?: boolean }): void;
  /** ASSIGN of the commission card (v2 §10, R2629, R2645): one own fleet
   *  to one open offer of the board. The hub judges the press. */
  assignCommission(id: number, fleetId: number): void;
  /** One cache intent of the cache sheet (v2 §9.3, §9.4, R2649): DEPOSIT
   *  from an own fleet at its position, or the NOTE of an own cache. The
   *  hub judges it, and a refusal arrives as an error notice. */
  cacheAction(a: CacheAction): void;
  /** SEARCH of the SEARCH picker (v2 §9.1, R2699): the fleet sails to the
   *  centre of the cell and searches it. The hub judges the order. */
  searchAt(fleetId: number, cell: number): void;
  /** SEARCH of the SEARCH picker at the point of a wreck (R2860): the
   *  fleet sails to (x, y) world px and searches there. */
  searchPoint(fleetId: number, x: number, y: number): void;
  /**
   * REPORT one piece of content through the game server (R1323). The
   * server sends no answer, so the control reads REPORTED at once.
   */
  report(target: GameReport, reason: ReportReason, note: string): void;
  /**
   * REPORT one line of the clan chat through the meta server
   * (`POST /api/reports`, deployment.md §7.6). The promise rejects with
   * the route's refusal.
   */
  reportClanLine(seq: number, reason: ReportReason, note: string): Promise<void>;
  /**
   * BLOCK one account in the clan chat (`PUT /api/blocks/{publicId}`).
   * It answers the public ids of the whole block list.
   */
  blockAccount(publicId: string): Promise<string[]>;
  /**
   * BLOCK one commander of this world through the game server (R1328),
   * by the world account id. The server sends no answer: it hides the
   * next line and stores the block in the account's one block list.
   */
  blockCommander(accountId: number): void;
  /**
   * Recall one control group (R539). The first press selects the fleet; a
   * second press on the group already selected centres the camera on it.
   */
  recallGroup(slot: number): void;
  /** Back out of every armed pick mode — what esc and a right-click do.
   * A touchscreen has neither, so the CANCEL press of the armed line calls this (R2164). */
  cancelModes(): void;
  /** Drop the selection — what esc and a click on empty water do. The ✕
   * DESELECT button in the selection panel calls this. */
  deselect(): void;
  /**
   * End the session and drop this world's resume token, then reload into
   * the landing screen.
   *
   * Both halves matter. The account session lives in a cookie the overlay
   * must not touch, and the seat lives in a per-world token in
   * localStorage; forgetting one and keeping the other puts the player
   * straight back where they were.
   */
  logout(): void;
  /**
   * Leave the world and go back to the signed-in hub (R236).
   *
   * It is not a quiet logout. The session stays and the world's resume
   * token stays, so the hub draws the seat and RESUME returns to it. The
   * empire keeps playing while nobody sits at it, exactly as it does when a
   * player closes the tab.
   *
   * The address bar has to be cleaned first. `rememberWorld` writes
   * `?world=N` so a reload lands back in the same world, and `resolveEntry`
   * deep-links on it — a plain reload would rejoin the world and never show
   * the hub. The implementation drops the parameter before it reloads.
   */
  menu(): void;
  /**
   * Leave the world for the arcade page (R1731): ARCADE beside MENU in the
   * session block of ☸ OPTIONS. The world goes down the way MENU takes it
   * down; the session and the seat stay.
   */
  arcade(): void;
  /**
   * Leave the world for another page of the site, the way MENU leaves
   * (R1731): a hub row of the ACCOUNT page opens the part of the hub that
   * holds the setting (`ui/hublink.ts`).
   */
  leaveFor(href: string): void;
  /**
   * The audio settings changed (plan §5): hand them to the director now.
   *
   * The overlay owns `settings` and writes it, exactly as the display
   * switches already do — this only says "apply it". Without it a flipped
   * switch or a dragged slider waits for the 1 Hz sweep in main.ts, and a
   * volume you cannot hear move is a volume that looks broken.
   */
  audioApply(): void;
  /** One media-player transport op. The overlay never imports the audio
   * director; main.ts owns it and answers these. `arg` is the shuffle
   * state (0/1) for 'shuffle' and the track index for 'jump'. */
  audioOp(op: AudioOp, arg?: number): void;
  /**
   * Refuse an order here, before it reaches the wire: a sell button pressed
   * with no price in the field is the same answer the server gives when it
   * refuses one, and it must look and sound the same.
   *
   * The overlay never imports the event bus, for the reason it never
   * imports the audio director: main.ts holds both and answers this. The
   * bus sends it through the `refusal` pseudo-event, so an operator who
   * silences refusals silences these too.
   */
  refuse(text: string): void;
}

/** What the media player's buttons ask for. One op with an argument rather
 * than seven closures, the way `fleetOp` and `admin` already read. */
export type AudioOp = 'play' | 'pause' | 'stop' | 'next' | 'prev' | 'shuffle' | 'jump';

/**
 * The cover state (R1755, ui-map-interface.md §10.5): what covers the map
 * of a telephone. `sheet` is the sheet at its open height, `menu` the
 * opaque OPTIONS menu, and `none` everything else, a desktop always. The
 * overlay states it, and the frame loop of the page reads it
 * (`fast/frame.ts` `framePeriod`).
 */
export type Cover = 'none' | 'sheet' | 'menu';

/**
 * Everything the settings card and the media player draw, pushed in by
 * main.ts (`setAudio`). The shape is the audio director's, restated here so
 * this file imports nothing from `audio/` — the overlay must not be able to
 * reach the sound engine except through `UIActions`.
 */
export interface UIAudio {
  /** False where the host published no manifest: the card says so and the
   * media player's top-bar button is not drawn at all. */
  available: boolean;
  playlists: { id: string; title: string }[];
  themes: { id: string; title: string }[];
  /** Null until there is a playlist to speak about. */
  status: {
    playlist: string;
    playlistTitle: string;
    /** Index into `tracks`, not into the shuffled play order. */
    trackIndex: number;
    title: string;
    time: number;
    duration: number;
    playing: boolean;
    shuffle: boolean;
    tracks: { title: string }[];
  } | null;
}

/**
 * The pick modes, as the fields that hold them (R469). Every one names the
 * fleet that waits for a target, and 0 is "not armed". `aiming` is not here:
 * it holds `null` for off and 0 for the hold's own Torpedo Battery, so it needs
 * its own handling everywhere.
 */
export const PICK_MODES = [
  'escorting', 'patrolling', 'hunting', 'moving', 'gathering', 'deploying',
  // SEARCH of the ⋯ of a fit hull (v2 §9.6, R2834): a cell.
  'searching',
] as const;

/** One pick mode, by the field that holds it. */
export type PickMode = typeof PICK_MODES[number];
